/**
 * SimpleModel — 表单字段模型
 *
 * ## 架构角色
 * 每个表单控件（input/select/reference/checkbox 等）在运行时对应一个 SimpleModel 实例。
 * 继承 BaseModel，负责字段值的读写、校验、联动规则、公式触发。
 *
 * ## 核心方法
 *
 * ### setValue(value) — 赋值入口（最重要）
 * ```
 * setValue → 多语兼容 → _triggerFormulaUpdate（注册 root._fieldModels[field]=this）
 *         → _.isEqual 新旧比对（相同跳过）
 *         → execute('beforeValueChange') — 扩展可阻止
 *         → _set_data 持久化
 *         → 相关性规则（relevantList 字段联动）
 *         → listener.setState → React 重渲染
 *         → execute('afterValueChange')
 * ```
 *
 * ### getValue() — 取值
 * 等同于 `model.getData()` → `model._get_data('value')`
 *
 * ### clear(useDefault) — 清空
 * useDefault=true 恢复默认值，false 置 null
 *
 * ### setData(value) — 外部设值入口
 * 等同 `setValue(value)`，给扩展脚本调用
 *
 * ## 如何获取字段模型
 * - 运行时通过 `root._fieldModels[fieldName]` 查找（formulaMixin 维护）
 * - 全局通过 `ttx.get(fieldName)` 查找
 *
 * ## 使用示例
 * ```js
 * var fieldModel = new ttx.models.SimpleModel({ field: 'name', cControlType: 'input' });
 * fieldModel.setValue('张三');  // 走完整链路：校验→持久化→通知→公式
 * var val = fieldModel.getValue(); // '张三'
 * fieldModel.on('afterValueChange', fn);
 * ```
 */
import _ from 'lodash';

ttx.models.register('SimpleModel', function (modelType) {
  const simpleModel = function (data) {
    // 调用 BaseModel 构造函数，将 meta JSON（含 cItemName/cControlType/cFormula 等）存入 _data 闭包
    ttx.models.BaseModel.call(this, data);
  };
  simpleModel.prototype = ttx.utils.getPrototype(ttx.models.BaseModel.prototype);
  simpleModel.prototype.modelType = modelType;

  // 将当前模型的所有属性变更通知到指定监听器（React 组件）
  simpleModel.prototype.updateListenerState = function (listener) {
    const propertyNames = this._get_data('propertyNames');
    const state = {};
    // 性能优化 foreach改成for循环
    for (let index = 0, len = propertyNames.length; index < len; index++) {
      const propertyName = propertyNames[index];
      let data = this._get_data(propertyName);
      // value 属性需要深拷贝，防止引用被外部修改
      if (propertyName === 'value')
        data = getNewValue(data);
      state[propertyName] = data;
    }
    // 优先使用 setListenerState（组件可自定义），兜底 setState
    if (this.notifyListener(listener, 'setListenerState', state)) return;
    this.notifyListener(listener, 'setState', state);
  };

  // 对外的设值入口，内部调用 setValue
  simpleModel.prototype.setData = function (data, isCharacter) {
    this.setValue(data, false, isCharacter);
  };

  // 获取字段默认值（来自 meta 中的 cDefaultValue）
  simpleModel.prototype.getDefaultData = function () {
    const defaultValue = this._get_data('cDefaultValue');
    const rootParent = this.getRootParent();
    const query = rootParent?.getParams?.()?.query || {};
    let newDefaultValue = ttx.utils.changePredicateDate(defaultValue, query);
    newDefaultValue = formatDefaultValue.call(this, newDefaultValue);
    return newDefaultValue;
  };

  // 清空字段值并恢复默认值（useDefault=false 时清空为 null）
  simpleModel.prototype.clear = function (useDefault) {
    this.setCache('isClearCall', true);
    if (useDefault === false) {
      this.setValue();
      this.setCache('isClearCall', false);
      return;
    }
    const defaultValue = this.getDefaultData();
    this.setValue(defaultValue);
    if (!this.get('bCheck')) {
      if (defaultValue == null) {
        this.setCache('isClearCall', false);
        return;
      }
      this.execute('afterValueChange', { value: defaultValue, async: false });
    }
    this.setCache('isClearCall', false);
  };

  // 根据控件类型格式化默认值（numberwidget 转 Number，switch 转 Boolean，inputmultilang 解析 JSON 等）
  const formatDefaultValue = function (defaultValue) {
    const controlType = this._get_data('cControlType') ? this._get_data('cControlType').toLowerCase() : this._get_data('cControlType');
    const defaultControlValue = {};
    if (defaultValue && controlType && controlType.toLowerCase() == 'inputmultilang' && typeof defaultValue != 'object') {
      try { defaultValue = JSON.parse(defaultValue); } catch (e) { ttx.console.error(Error.E02003, '[formatDefaultValue]: defaultValue should be a JSON string', defaultValue, e) }
    } else if (controlType && controlType.toLowerCase() == 'numberwidget') {
      // 新建时数值控件的初始值应为空, Number('')是0
      defaultValue = defaultValue === ''
        ? defaultValue
        : (isNaN(defaultValue) ? defaultValue : Number(defaultValue))
    } else if (controlType && (controlType.toLowerCase() == 'rate' || controlType.toLowerCase() == 'money')) {
      defaultValue = isNaN(defaultValue) ? defaultValue : Number(defaultValue);
    } else if (controlType && controlType.toLowerCase() == 'switch') {
      if (defaultValue == '0' || defaultValue === 'false') {
        defaultValue = false;
      } else {
        defaultValue = Boolean(defaultValue);
      }
    }
    if (defaultControlValue[controlType]) {
      defaultValue = defaultValue || defaultControlValue[controlType]
    }
    return defaultValue
  }

  // 深拷贝值对象/数组，防止引用污染
  const getNewValue = function (value) {
    if (value == null || typeof value !== 'object')
      return value;
    if (ttx.utils.isArray(value))
      return ttx.utils.extend(true, [], value);
    return ttx.utils.extend(true, {}, value);
  };

  // 内部设值：翻译符号 → 通知 doPropertyChange → 逐个 listener.setState
  const setValue = function (value) {
    value = ttx.utils.translationSymbols(value)
    const notNotifiedListeners = this.doPropertyChange('setValue', value);
    if (!notNotifiedListeners) return;
    const state = { value: value };
    notNotifiedListeners.forEach(function (listener) {
      this.notifyListener(listener, 'setState', state);
    }, this);
  }

  /**
   * 设置字段值（核心方法）
   *
   * 执行流程：
   * 1. 多语文本兼容（zh_CN 等语种取值）
   * 2. 新旧值对比（_.isEqual），相同则跳过
   * 3. beforeValueChange 事件（可被扩展脚本阻止）
   * 4. _set_data 持久化到闭包
   * 5. 相关性规则计算（relevantList 联动）
   * 6. 通知监听器（React 组件 setState 触发重渲染）
   * 7. afterValueChange / afterInnerValueChange 事件
   * 8. 公式实时计算（_triggerFormulaUpdate）
   *
   * @param value   - 新值
   * @param check   - 是否触发了前置校验（前台校验设置的值）
   * @param isCharacter - 是否多语字符模型内部联动
   */
  simpleModel.prototype.setValue = function (value, check, isCharacter) {
    value = ttx.utils.translationSymbols(value)
    // 兼容多语文本给普通字段赋值场景：多语对象 { zh_CN: 'xx', en_US: 'yy' } → 取当前语种
    if (typeof value == 'object' && (value?.hasOwnProperty('zh_CN') || ttx.utils.isEmptyObject(value))) {
      if (this._get_data('cControlType')?.toLowerCase() !== 'inputmultilang') {
        const locale = ttx.rest.AppContext.globalization?.locale || 'zh_CN';
        value = value?.[locale] || null;
      }
    }
    // 新旧值深度比较：相同则直接返回，避免无效渲染
    // 注册字段模型到根 VM + 调度公式计算（必须在 isEqual 之前，否则 undefined 初始值会跳过注册）
    // check=true 是前台校验触发的 setValue，isCharacter=true 是多语字段内部联动，这两类无需触发公式
    if (!check && !isCharacter) {
      this._triggerFormulaUpdate();
    }
    const oldValue = this.getValue();
    if (_.isEqual(value, oldValue)) {
      return;
    }
    const mode = this.getRootParent?.()?.getParams?.()?.mode
    // 编辑变更历史特性：编辑模式下记录旧值用于历史追踪
    const editChangeHistory = this.hasFeature('editChangeHistory')
    if (editChangeHistory && (mode === 'edit')) this.setEditPrevvalue(oldValue)
    // saveNullSensitive 特性：undefind → null 不触发赋值（减少无效变更）
    const saveNullSensitive = this.hasFeature('saveNullSensitive');
    const des = this.get?.('desensitization')
    // 脱敏字段置空也要更新（focus 清空 value 触发参照代值的场景）
    if (!des && !saveNullSensitive && ttx.utils.isEmpty(value) && ttx.utils.isEmpty(oldValue)) return;
    const data = { value: value, oldValue: oldValue };
    // forceFireEvent：设置了 forceFireEvent 且非加载数据/清空时，强制触发变更事件
    const forceFireEvent = this._get_data('forceFireEvent') && !this.getRootParent?.()?.getCache('isLoadData') && !this.getCache('isClearCall');
    // beforeValueChange 前置事件：返回 false 则阻止赋值（invalidReset 控制是否回滚旧值）
    if ((check || forceFireEvent) && !this.execute('beforeValueChange', data)) {
      if (this._get_data('invalidReset') !== false)
        setValue.call(this, oldValue);
      return;
    }
    // 持久化到闭包 _data
    this._set_data('value', value, true);
    // 增加相关性规则处理：向上遍历找到 GridModel/CharacterModel 判断是否为表格行编辑
    let parentModel = this?.getParent?.();
    let model = parentModel?.getParent?.()
    const cItemName = this._get_data('cItemName');
    let updatingEditRowModel = false;
    if (parentModel instanceof ttx.models.CharacterModel) {
      parentModel = parentModel?.getParent?.();
      model = model?.getParent?.();
    }
    // 判断是否在表格中（GridModel），且当前变更不是表格正在更新的单元格
    if (model instanceof ttx.models.GridModel) {
      const updatingCellName = model?.getCache('updatingCellName');
      if (cItemName !== updatingCellName) {
        updatingEditRowModel = parentModel?.getCache?.('updatingEditRowModel') || false;
        if (model?.getCache('referCarrierMap') && model?.getCache('referCarrierMap')?.hasOwnProperty(cItemName)) {
          updatingEditRowModel = false;
        }
      }
    }
    let updatingCharacterModel = false;
    if (this?.getParent?.() instanceof ttx.models.CharacterModel) {
      updatingCharacterModel = this?.getParent?.()?.getCache?.('updatingCharacterModel') || false;
    }
    // 相关性规则：字段间联动（如选A则B可见/必填），非校验、非字符、非表格行更新时触发
    if (!ttx.utils.isEmptyObject(this._get_data('relevantList')) && (!check || !isCharacter) && !updatingEditRowModel && !updatingCharacterModel) {
      const relevantList = this._get_data('relevantList');
      ttx.relevant.dealRelevantRule(this, value, relevantList, this.getDomainKey());
    }
    const newValue = getNewValue(value);
    // 通知所有监听器（React 组件）更新渲染
    setValue.call(this, newValue);
    (check || forceFireEvent) && this.execute('afterValueChange', data);
    this.execute('afterInnerValueChange', data, check);
  };


  simpleModel.prototype.getValue = function () {
    return this.getData();
  };

  // 设置上一次的值（用于变更历史对比）
  simpleModel.prototype.setPrevValue = function (value) {
    const oldValue = this._get_data('prevvalue');
    if (_.isEqual(value, oldValue)) return;
    if (ttx.utils.isEmpty(value) && ttx.utils.isEmpty(oldValue)) return;
    this._set_data('prevvalue', value);
    const notNotifiedListeners = this.doPropertyChange('setPrevValue', value);
    if (!notNotifiedListeners) return;
    const state = { prevvalue: value };
    notNotifiedListeners.forEach(function (listener) {
      this.notifyListener(listener, 'setState', state);
    }, this);
  };

  simpleModel.prototype.setStyle = function (style) {
    this._set_data('style', style);
  };

  simpleModel.prototype.getStyle = function () {
    return this._get_data('style');
  };

  return simpleModel;
});
